const localApiBaseUrl =
  process.env.NODE_ENV === "development" &&
  typeof window !== "undefined" &&
  /^(localhost|127\.0\.0\.1|0\.0\.0\.0|::1)$/.test(window.location.hostname) &&
  window.location.port &&
  window.location.port !== "3000"
    ? ["http://", "localhost", ":3000"].join("")
    : "";
const apiBaseUrl = (process.env.REACT_APP_PRODUCTION_API_URL || localApiBaseUrl).replace(/\/$/, "");

const AWC_AUTH_EXPIRED_EVENT = "awc-auth-expired";
const AWC_SESSION_KEYS = [
  "newHrmSession",
  "awcUser",
  "awcAccessToken",
  "awcTokenExpiresAt",
  "authTokenForGoogleLogin",
  "contactUserId",
  "contactUserName",
];

const clearStoredAwcSession = () => {
  AWC_SESSION_KEYS.forEach((key) => {
    try {
      localStorage.removeItem(key);
    } catch {
      // Storage restrictions must not prevent session-expiry handling.
    }
  });
};

const notifyAwcSessionExpired = () => {
  clearStoredAwcSession();
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent(AWC_AUTH_EXPIRED_EVENT));
  }
};

const buildQuery = (query = {}) => {
  const params = new URLSearchParams();
  Object.entries(query).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      params.set(key, value);
    }
  });
  const queryString = params.toString();
  return queryString ? `?${queryString}` : "";
};

const withRequestDeadline = async (run, { signal, timeoutMs, readOnly }) => {
  const controller = new AbortController();
  const abort = () => controller.abort();
  if (signal?.aborted) abort();
  else signal?.addEventListener("abort", abort, { once: true });
  let timedOut = false;
  const timer = setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, timeoutMs);
  try {
    return await run(controller.signal);
  } catch (error) {
    if (timedOut) {
      throw new Error(readOnly
        ? "HRM is taking too long to respond. Please retry."
        : "The request timed out. Refresh and check whether your changes were saved before trying again.");
    }
    throw error;
  } finally {
    clearTimeout(timer);
    signal?.removeEventListener("abort", abort);
  }
};

const request = (path, options = {}) => {
  const readOnly = !options.method || options.method.toUpperCase() === "GET";
  return withRequestDeadline(
    (signal) => performRequest(path, { ...options, signal }),
    { signal: options.signal, timeoutMs: readOnly ? 45000 : 90000, readOnly }
  );
};

const performRequest = async (path, options = {}) => {
  const { auth = true, query, body, ...fetchOptions } = options;
  const headers = {
    "Content-Type": "application/json",
    ...(fetchOptions.headers || {}),
  };

  const response = await fetch(`${apiBaseUrl}/backend/${path.replace(/^\//, "")}${buildQuery(query)}`, {
    ...fetchOptions,
    credentials: "include",
    headers,
    body: body === undefined ? undefined : JSON.stringify(body),
  });

  if (fetchOptions.signal?.aborted) throw new DOMException("Aborted", "AbortError");

  if (response.status === 401 && auth !== false) {
    notifyAwcSessionExpired();
    throw Object.assign(new Error("Session expired. Please sign in again."), { status: 401 });
  }

  const text = await response.text();
  if (fetchOptions.signal?.aborted) throw new DOMException("Aborted", "AbortError");
  let data = null;

  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      if (auth !== false || response.ok) {
        const looksLikeHtml = text.trim().startsWith("<");
        throw new Error(
          looksLikeHtml
            ? "Backend API route returned HTML. Restart the backend so the latest HRM APIs are active."
            : "Backend API returned invalid JSON."
        );
      }
    }
  }

  if (!response.ok || data?.error || data?.success === false) {
    if (auth === false) {
      const fallback = response.status === 401
        ? "Sign-in could not be verified. Please try again."
        : `Request failed with status ${response.status}`;
      const message = typeof data?.message === "string" && data.message.trim() ? data.message : fallback;
      throw Object.assign(new Error(message), { status: response.status });
    }

    if (/awc token|bearer token/i.test(data?.message || "")) {
      notifyAwcSessionExpired();
      throw Object.assign(new Error("Session expired. Please sign in again."), { status: 401 });
    }

    throw new Error(data?.message || `Request failed with status ${response.status}`);
  }

  return data;
};

const getResponseFileName = (response) => {
  const disposition = response.headers.get("content-disposition") || "";
  const encodedName = disposition.match(/filename\*=UTF-8''([^;]+)/i)?.[1];
  if (encodedName) {
    try {
      return decodeURIComponent(encodedName);
    } catch {
      return encodedName;
    }
  }
  return disposition.match(/filename="([^"]+)"/i)?.[1] || disposition.match(/filename=([^;]+)/i)?.[1]?.trim() || "";
};

const requestFile = (path, options = {}) => withRequestDeadline(
  (signal) => performFileRequest(path, { ...options, signal }),
  { signal: options.signal, timeoutMs: 120000, readOnly: true }
);

const performFileRequest = async (path, options = {}) => {
  const { query, ...fetchOptions } = options;
  const response = await fetch(`${apiBaseUrl}/backend/${path.replace(/^\//, "")}${buildQuery(query)}`, {
    ...fetchOptions,
    credentials: "include",
  });

  if (!response.ok) {
    const text = await response.text();
    let data = null;
    try {
      data = text ? JSON.parse(text) : null;
    } catch {
      data = null;
    }
    if (response.status === 401 || /awc token|bearer token/i.test(data?.message || "")) {
      notifyAwcSessionExpired();
      throw new Error("Session expired. Please sign in again.");
    }
    throw new Error(data?.message || `Download failed with status ${response.status}`);
  }

  const responseContentType = response.headers.get("content-type") || "";
  if (/application\/json/i.test(responseContentType)) {
    const data = await response.json();
    throw new Error(data?.message || "The payroll service did not return a PDF file.");
  }

  const blob = await response.blob();
  if (!blob.size) {
    throw new Error("The downloaded salary slip is empty.");
  }
  const contentType = responseContentType || blob.type || "";
  if (contentType && !/application\/(?:pdf|octet-stream)/i.test(contentType)) {
    throw new Error("The payroll service returned an invalid salary slip file.");
  }
  return {
    blob,
    contentType,
    fileName: getResponseFileName(response),
  };
};

export const loginWithGoogle = (credential) =>
  request("awc/v1/auth/google", {
    auth: false,
    method: "POST",
    body: { credential },
  });

export const logoutFromHrm = () =>
  request("awc/v1/auth/logout", {
    method: "POST",
  });

export const getCurrentHrmSession = ({ signal } = {}) =>
  request("awc/v1/auth/me", {
    method: "GET",
    cache: "no-store",
    signal,
  });

const RONIN_PAGE_PATH = "/backend/awc/v1/hrm/games/ronin/page";

export const getRoninProgress = ({ signal, playerKey } = {}) =>
  request('awc/v1/hrm/games/ronin/progress', { method: 'GET', cache: 'no-store', signal, query: { playerKey } });
export const saveRoninProgress = (body, { signal, playerKey } = {}) =>
  request('awc/v1/hrm/games/ronin/progress', { method: 'PUT', body, signal, query: { playerKey } });
export const getRoninLeaderboard = ({ signal, playerKey } = {}) =>
  request('awc/v1/hrm/games/ronin/leaderboard', { method: 'GET', cache: 'no-store', signal, query: { playerKey } });

export const getRoninGame = async ({ signal } = {}) => {
  const response = await request("awc/v1/hrm/games/ronin", {
    method: "GET",
    cache: "no-store",
    signal,
  });
  const data = response?.data;
  if (response?.success !== true || data?.pagePath !== RONIN_PAGE_PATH ||
      typeof data?.version !== "string" || !data.version.trim() ||
      typeof data?.playerKey !== "string" || !/^[a-f0-9]{32}$/.test(data.playerKey)) {
    throw new Error("Ronin is temporarily unavailable. Please retry.");
  }
  return { ...data, pageUrl: `${apiBaseUrl}${RONIN_PAGE_PATH}` };
};

export const getHrmAccessStatus = () =>
  request("awc/v1/security/hrm-access", {
    auth: false,
    cache: "no-store",
    method: "GET",
  });

export const getConfig = (viewUserId) =>
  request("awc/v1/hrm/config", { query: { viewUserId } });

export const getDashboard = (viewUserId, month) =>
  request("awc/v1/hrm/dashboard", { query: { viewUserId, month } });

export const getDefaulterLeaderboard = () =>
  request("awc/v1/hrm/leaderboard/defaulters", { method: "GET" });

export const getManagedUsers = (query = {}) => request("awc/v1/hrm/admin/users", { query });

export const getEmployees = (query = {}) => request("awc/v1/hrm/employees", { query });

export const getTodayAttendance = (viewUserId) =>
  request("awc/v1/hrm/attendance/today", { query: { viewUserId } });

export const checkIn = (viewUserId, payload = {}) =>
  request("awc/v1/hrm/attendance/check-in", {
    method: "POST",
    query: { viewUserId },
    body: payload,
  });

export const checkOut = (viewUserId, payload = {}) =>
  request("awc/v1/hrm/attendance/check-out", {
    method: "POST",
    query: { viewUserId },
    body: payload,
  });

export const getAttendanceLogs = (viewUserId, range = {}) =>
  request("awc/v1/hrm/attendance/logs", {
    query: { viewUserId, ...range },
  });

export const createAttendanceCorrectionRequest = (viewUserId, payload) =>
  request("awc/v1/hrm/attendance/correction-request", {
    method: "POST",
    query: { viewUserId },
    body: payload,
  });

export const getLeaveBalances = (viewUserId) =>
  request("awc/v1/hrm/leaves/balances", { query: { viewUserId } });

export const getLeaveCredits = (viewUserId) =>
  request("awc/v1/hrm/leaves/credits", { query: { viewUserId } });

export const getLeaveRequests = (viewUserId, filters = {}) =>
  request("awc/v1/hrm/leaves/requests", {
    query: { viewUserId, ...filters },
  });

export const getLeaveTypes = (viewUserId) =>
  request("awc/v1/hrm/leaves/types", { query: { viewUserId } });

export const getHolidays = (viewUserId, year) =>
  request("awc/v1/hrm/leaves/holidays", {
    query: { viewUserId, year },
  });

export const createLeaveRequest = (viewUserId, payload) =>
  request("awc/v1/hrm/leaves/requests", {
    method: "POST",
    query: { viewUserId },
    body: payload,
  });

export const updateLeaveRequest = (viewUserId, requestId, payload) =>
  request(`awc/v1/hrm/leaves/requests/${encodeURIComponent(requestId)}`, {
    method: "PATCH",
    query: { viewUserId },
    body: payload,
  });

export const cancelLeaveRequest = (viewUserId, requestId, payload = {}) =>
  request(`awc/v1/hrm/leaves/requests/${encodeURIComponent(requestId)}/cancel`, {
    method: "PATCH",
    query: { viewUserId },
    body: payload,
  });

export const getLeaveApprovals = (viewUserId, filters = {}) =>
  request("awc/v1/hrm/leaves/approvals", {
    query: { viewUserId, ...filters },
  });

export const approveLeaveRequest = (viewUserId, requestId, payload = {}) =>
  request(`awc/v1/hrm/leaves/requests/${encodeURIComponent(requestId)}/approve`, {
    method: "PATCH",
    query: { viewUserId },
    body: payload,
  });

export const rejectLeaveRequest = (viewUserId, requestId, payload = {}) =>
  request(`awc/v1/hrm/leaves/requests/${encodeURIComponent(requestId)}/reject`, {
    method: "PATCH",
    query: { viewUserId },
    body: payload,
  });

export const getTimeMonth = (viewUserId, month) =>
  request("awc/v1/hrm/time/month", { query: { viewUserId, month } });

export const saveTimeWeek = (viewUserId, month, weekId, payload) =>
  request(`awc/v1/hrm/time/weeks/${encodeURIComponent(weekId)}`, {
    method: "PUT",
    query: { viewUserId, month },
    body: payload,
  });

export const submitTimeWeek = (viewUserId, weekId, payload = {}) =>
  request(`awc/v1/hrm/time/weeks/${encodeURIComponent(weekId)}/submit`, {
    method: "POST",
    query: { viewUserId },
    body: payload,
  });

export const submitTimeEntries = (viewUserId, payload) =>
  request("awc/v1/hrm/time/entries/submit", {
    method: "POST",
    query: { viewUserId },
    body: payload,
  });

export const searchProjects = (viewUserId, search = "", extraQuery = {}) =>
  request("awc/v1/hrm/projects/search", {
    query: { viewUserId, search, page: 1, pageSize: 100, ...extraQuery },
  });

export const searchProjectAccounts = (viewUserId, search = "") =>
  request("awc/v1/hrm/projects/accounts", {
    query: { viewUserId, search, page: 1, pageSize: 25 },
  });

export const createProject = (viewUserId, payload) =>
  request("awc/v1/hrm/projects", {
    method: "POST",
    query: { viewUserId },
    body: payload,
  });

export const getProjectDetail = (viewUserId, projectId) =>
  request(`awc/v1/hrm/projects/${encodeURIComponent(projectId)}`, {
    query: { viewUserId },
  });

export const addProjectSentiment = (viewUserId, projectId, payload) =>
  request(`awc/v1/hrm/projects/${encodeURIComponent(projectId)}/sentiments`, {
    method: "POST",
    query: { viewUserId },
    body: payload,
  });

export const getProjectEntities = (viewUserId, filters = {}) => {
  const queryFilters = typeof filters === "string" ? { search: filters } : filters;
  return request("awc/v1/hrm/projects/entities", {
    query: { viewUserId, ...queryFilters, page: 1, pageSize: 200 },
  });
};

export const getProjectEntity = (viewUserId, entityId) =>
  request(`awc/v1/hrm/projects/entities/${encodeURIComponent(entityId)}`, {
    query: { viewUserId },
  });

export const updateProjectEntity = (viewUserId, entityId, payload) =>
  request(`awc/v1/hrm/projects/entities/${encodeURIComponent(entityId)}`, {
    method: "PATCH",
    query: { viewUserId },
    body: payload,
  });

export const createProjectEntity = (viewUserId, payload) =>
  request("awc/v1/hrm/projects/entities", {
    method: "POST",
    query: { viewUserId },
    body: payload,
  });

export const addProjectEntityComment = (viewUserId, entityId, payload) =>
  request(`awc/v1/hrm/projects/entities/${encodeURIComponent(entityId)}/comments`, {
    method: "POST",
    query: { viewUserId },
    body: payload,
  });

export const addProjectEntityChecklistItem = (viewUserId, entityId, payload) =>
  request(`awc/v1/hrm/projects/entities/${encodeURIComponent(entityId)}/checklist`, {
    method: "POST",
    query: { viewUserId },
    body: payload,
  });

export const updateProjectEntityChecklistItem = (viewUserId, entityId, checklistItemId, payload) =>
  request(
    `awc/v1/hrm/projects/entities/${encodeURIComponent(entityId)}/checklist/${encodeURIComponent(checklistItemId)}`,
    {
      method: "PATCH",
      query: { viewUserId },
      body: payload,
    }
  );

export const searchTasks = (viewUserId, projectId = "", search = "") =>
  request("awc/v1/hrm/tasks/search", {
    query: { viewUserId, projectId, search, page: 1, pageSize: 50 },
  });

export const getProfile = (viewUserId) =>
  request("awc/v1/hrm/profile", { query: { viewUserId } });

export const updateProfile = (viewUserId, payload) =>
  request("awc/v1/hrm/profile", {
    method: "PUT",
    query: { viewUserId },
    body: payload,
  });

export const updateProfilePersonalContact = (viewUserId, payload) =>
  request("awc/v1/hrm/profile/personal-contact", {
    method: "PATCH",
    query: { viewUserId },
    body: payload,
  });

export const updateEmergencyContacts = (viewUserId, payload) =>
  request("awc/v1/hrm/profile/emergency-contacts", {
    method: "PUT",
    query: { viewUserId },
    body: payload,
  });

export const updateInsuranceRelated = (viewUserId, payload) =>
  request("awc/v1/hrm/profile/insurance-related", {
    method: "PUT",
    query: { viewUserId },
    body: payload,
  });

export const updateAddresses = (viewUserId, payload) =>
  request("awc/v1/hrm/profile/addresses", {
    method: "PUT",
    query: { viewUserId },
    body: payload,
  });

export const uploadProfileDocument = (viewUserId, payload) =>
  request("awc/v1/hrm/files/profile-document", {
    method: "POST",
    query: { viewUserId },
    body: payload,
  });

export const deleteProfileDocument = (viewUserId, documentId) =>
  request(`awc/v1/hrm/files/${encodeURIComponent(documentId)}/delete`, {
    method: "POST",
    query: { viewUserId },
  });

export const getPayrollSummary = (viewUserId) =>
  request("awc/v1/hrm/payroll/summary", { query: { viewUserId } });

export const getPayrollSlips = (viewUserId, year) =>
  request("awc/v1/hrm/payroll/slips", { query: { viewUserId, year } });

export const getPayrollSlip = (viewUserId, slipId) =>
  request(`awc/v1/hrm/payroll/slips/${encodeURIComponent(slipId)}`, {
    query: { viewUserId },
  });

export const downloadPayrollSlip = (viewUserId, slipId) =>
  requestFile(`awc/v1/hrm/payroll/slips/${encodeURIComponent(slipId)}/download`, {
    query: { viewUserId },
  });

export const getRequests = (viewUserId, filters = {}) =>
  request("awc/v1/hrm/requests", { query: { viewUserId, ...filters } });

export const getRequest = (viewUserId, requestId) =>
  request(`awc/v1/hrm/requests/${encodeURIComponent(requestId)}`, {
    query: { viewUserId },
  });

export const createRequest = (viewUserId, payload) =>
  request("awc/v1/hrm/requests", {
    method: "POST",
    query: { viewUserId },
    body: payload,
  });

export const updateRequest = (viewUserId, requestId, payload) =>
  request(`awc/v1/hrm/requests/${encodeURIComponent(requestId)}`, {
    method: "PATCH",
    query: { viewUserId },
    body: payload,
  });

export const createReferral = (viewUserId, payload) =>
  request("awc/v1/hrm/referrals", {
    method: "POST",
    query: { viewUserId },
    body: payload,
  });

export const addRequestComment = (viewUserId, requestId, payload) =>
  request(`awc/v1/hrm/requests/${encodeURIComponent(requestId)}/comments`, {
    method: "POST",
    query: { viewUserId },
    body: payload,
  });

export const getExpenses = (viewUserId, filters = {}) =>
  request("awc/v1/hrm/expenses", { query: { viewUserId, ...filters } });

export const createExpense = (viewUserId, payload) =>
  request("awc/v1/hrm/expenses", {
    method: "POST",
    query: { viewUserId },
    body: payload,
  });

export const updateExpense = (viewUserId, expenseId, payload) =>
  request(`awc/v1/hrm/expenses/${encodeURIComponent(expenseId)}`, {
    method: "PATCH",
    query: { viewUserId },
    body: payload,
  });

export const cancelExpense = (viewUserId, expenseId, payload = {}) =>
  request(`awc/v1/hrm/expenses/${encodeURIComponent(expenseId)}/cancel`, {
    method: "PATCH",
    query: { viewUserId },
    body: payload,
  });

export const submitExpense = (viewUserId, expenseId, payload = {}) =>
  request(`awc/v1/hrm/expenses/${encodeURIComponent(expenseId)}/submit`, {
    method: "POST",
    query: { viewUserId },
    body: payload,
  });

export const getDirectory = (viewUserId, search = "") =>
  request("awc/v1/hrm/directory", {
    query: { viewUserId, search, page: 1, pageSize: 500 },
  });

export const getPolicies = (viewUserId, search = "") =>
  request("awc/v1/hrm/policies", { query: { viewUserId, search } });

export const getPolicy = (viewUserId, policyId) =>
  request(`awc/v1/hrm/policies/${encodeURIComponent(policyId)}`, {
    query: { viewUserId },
  });

export const acknowledgePolicy = (viewUserId, policyId, payload = {}) =>
  request(`awc/v1/hrm/policies/${encodeURIComponent(policyId)}/acknowledge`, {
    method: "POST",
    query: { viewUserId },
    body: payload,
  });

export const getAssetAllocations = (viewUserId) =>
  request("awc/v1/hrm/assets/allocations", { query: { viewUserId } });

export const getBenefits = (viewUserId) =>
  request("awc/v1/hrm/benefits", { query: { viewUserId } });

export const getPerformance = (viewUserId) =>
  request("awc/v1/hrm/performance", { query: { viewUserId } });

export const getMonthlyUtilization = (viewUserId, year, { signal } = {}) =>
  request("awc/v1/hrm/performance/utilization", { query: { viewUserId, year }, signal, cache: "no-store" });

export const getPerformanceReviews = (viewUserId, filters = {}) =>
  request("awc/v1/hrm/performance/reviews", {
    query: { viewUserId, page: 1, pageSize: 50, ...filters },
  });

export const getPerformanceReview = (viewUserId, reviewId) =>
  request(`awc/v1/hrm/performance/reviews/${encodeURIComponent(reviewId)}`, {
    query: { viewUserId },
  });

export const getForms = (viewUserId) =>
  request("awc/v1/hrm/forms", {
    query: { viewUserId },
  });

export const getRetreatEvent = () =>
  request("awc/v1/hrm/events/retreat-2026");

export const getRetreatEventPageUrl = () =>
  `${apiBaseUrl}/backend/awc/v1/hrm/events/retreat-2026/page`;

export const getAppraisalForm = (viewUserId) =>
  request("awc/v1/hrm/forms/appraisal", {
    query: { viewUserId },
  });

export const saveAppraisalDraftForm = (viewUserId, payload = {}) =>
  request("awc/v1/hrm/forms/appraisal/draft", {
    method: "POST",
    query: { viewUserId },
    body: payload,
  });

export const submitAppraisalForm = (viewUserId, payload = {}) =>
  request("awc/v1/hrm/forms/appraisal/submit", {
    method: "POST",
    query: { viewUserId },
    body: payload,
  });

export const getLearning = (viewUserId, filters = {}) =>
  request("awc/v1/hrm/learning", { query: { viewUserId, page: 1, pageSize: 500, ...filters } });

export const getLearningCourse = (viewUserId, assignmentId) =>
  request(`awc/v1/hrm/learning/${encodeURIComponent(assignmentId)}`, {
    query: { viewUserId },
  });

export const acknowledgeLearningCourse = (viewUserId, assignmentId, payload = {}) =>
  request(`awc/v1/hrm/learning/${encodeURIComponent(assignmentId)}/acknowledge`, {
    method: "POST",
    query: { viewUserId },
    body: payload,
  });

export const getOnboardingTasks = (viewUserId) =>
  request("awc/v1/hrm/onboarding/tasks", { query: { viewUserId } });

export const createFileUploadSlot = (viewUserId, payload) =>
  request("awc/v1/hrm/files/upload-slot", {
    method: "POST",
    query: { viewUserId },
    body: payload,
  });

export const downloadFile = (viewUserId, fileId) =>
  request(`awc/v1/hrm/files/${encodeURIComponent(fileId)}/download`, {
    query: { viewUserId },
  });
